import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { CollapseFade, STAGE_TRANSITION_MS } from "./CollapseFade";
import { CommandChips } from "./CommandChips";
import { DisplayMessage } from "./DisplayMessage";
import { HeaderWithLogo } from "./HeaderWithLogo";
import { RequestTypeHints } from "./RequestTypeHints";
import { SuccessCloseTimer } from "./SuccessCloseTimer";
import { TranscriptBox, type TranscriptLine } from "./TranscriptBox";
import { UndeliveredNotice } from "./UndeliveredNotice";
import type { RequestTypeItem } from "@/lib/elevenlabs/agent";

type Props = {
  /** The shared orb, already configured for the current phase. */
  orb: ReactNode;
  status: string;
  /**
   * Request-fulfilled confirmation phase: hints and transcript give way to
   * the confirmation copy, command chips and the close countdown.
   */
  success: boolean;
  /**
   * Notify failed: hide transcript/hints, show undelivered copy + 5s close.
   */
  undelivered?: boolean;
  /** Agent is waiting for the patient to speak. */
  listening?: boolean;
  /** ElevenLabs session is live (speaking / listening / success / undelivered). */
  agentConnected?: boolean;
  /** Current mic loudness `0..1` while listening. */
  micLevel?: number;
  requestTypes: RequestTypeItem[];
  lines: TranscriptLine[];
  /** Resting transcript copy until the first spoken turn. */
  placeholder?: string;
  /** Fired when the success/undelivered window's 5s countdown elapses. */
  onCloseElapsed?: () => void;
};

/** The in-room screen: orb, status copy, ask-for hints and the transcript. */
export function ConversationStage({
  orb,
  status,
  success,
  undelivered = false,
  listening = false,
  agentConnected = false,
  micLevel = 0,
  requestTypes,
  lines,
  placeholder,
  onCloseElapsed,
}: Props) {
  const closing = success || undelivered;
  const enterDelay = closing ? STAGE_TRANSITION_MS : 0;
  const leaveDelay = closing ? 0 : STAGE_TRANSITION_MS;

  return (
    <Box
      sx={{
        backgroundColor: "#080c14",
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <HeaderWithLogo agentConnected={agentConnected} />

      <Stack
        sx={{
          flex: 1,
          gap: 0,
          alignItems: "center",
          justifyContent: "flex-start",
          px: "24px",
          pt: "24px",
          pb: closing ? 0 : "24px",
        }}
      >
        <Stack sx={{ gap: 0, alignItems: "center", maxWidth: "100%" }}>
          <Stack sx={{ gap: 4, alignItems: "center" }}>
            <Box sx={{ pr: 1 }}>{orb}</Box>
            {undelivered ? (
              <UndeliveredNotice />
            ) : (
              <DisplayMessage status={status} success={success} />
            )}
          </Stack>

          <CollapseFade
            open={!closing && requestTypes.length > 0}
            spaceAbove={16}
            delayMs={leaveDelay}
            stretch
          >
            <RequestTypeHints items={requestTypes} />
          </CollapseFade>
        </Stack>

        <CollapseFade
          open={!closing && (lines.length > 0 || Boolean(placeholder))}
          spaceAbove={24}
          delayMs={leaveDelay}
        >
          <TranscriptBox
            lines={lines}
            placeholder={placeholder}
            listening={listening}
            micLevel={micLevel}
          />
        </CollapseFade>

        <CollapseFade open={success} spaceAbove={24} delayMs={enterDelay}>
          <Stack sx={{ gap: "16px", alignItems: "center" }}>
            <CommandChips text="Return to TV" />
            <CommandChips
              text="Another Request"
              backgroundColor="rgba(22, 199, 132, 0.16)"
              borderColor="rgba(22, 199, 132, 0.42)"
            />
          </Stack>
        </CollapseFade>

        <Box sx={{ flex: 1, minHeight: closing ? "24px" : 0 }} />
      </Stack>

      <SuccessCloseTimer
        open={closing}
        onElapsed={() => onCloseElapsed?.()}
      />
    </Box>
  );
}
